// Keepers: who each team keeps from last season, and what each costs
// (PLAN-2026-10-27-the-next-season §2.3, §4; scenario Q7).

import { z } from 'zod';
import { LeagueRef, PlayerSummary, TeamRef } from './views';

const Iso = z.string();

/** One player a team could keep: what he would cost, or why he cannot be kept. */
export const KeeperPlayer = z.object({
  player: PlayerSummary,
  /**
   * What keeping him costs: the round of the team's own pick he takes, his
   * price in an auction, or neither under the `none` rule (one of the team's
   * earliest picks), with the words a screen shows. Null when he cannot be kept.
   */
  cost: z
    .object({
      round: z.number().int().nullable(),
      price: z.number().int().nullable(),
      label: z.string(),
    })
    .nullable(),
  /** Why he cannot be kept: kept the most seasons the rules allow, a cost before the first round. Null when he can. */
  reason: z.string().nullable(),
  /** The seasons in a row he will have been kept, this one included; null when he cannot be kept. */
  years: z.number().int().nullable(),
  /** The team keeps him. */
  kept: z.boolean(),
  /**
   * The round of the pick he sits on once the team's keepers are placed, a
   * collision moved to the nearest free round before it (§2.3 rule 5); null
   * when he is not kept, and in an auction.
   */
  round: z.number().int().nullable(),
});
export type KeeperPlayer = z.infer<typeof KeeperPlayer>;

export const KeepersTeam = z.object({
  team: TeamRef,
  /** The reader may change this team's keepers now. */
  canEdit: z.boolean(),
  /** Its keepers in the order chosen, by player id: the order the earliest picks go in. */
  chosen: z.array(z.string()),
  /** Last season's roster, the players it could keep first. */
  players: z.array(KeeperPlayer),
});
export type KeepersTeam = z.infer<typeof KeepersTeam>;

/**
 * `GET /api/leagues/:id/keepers`: every team's keepers, which are public as
 * they are chosen (§2.3 rule 9), the reader's own team first.
 */
export const KeepersView = z.object({
  league: LeagueRef,
  mode: z.enum(['redraft', 'keeper', 'dynasty']),
  /** The most each team keeps. */
  count: z.number().int(),
  /** What a keeper costs, as the rules say it. */
  rule: z.string(),
  /** When a manager's choices lock: an hour before the draft. Null while the draft has no time. */
  deadline: Iso.nullable(),
  /** The draft has started, and every team's keepers are final. */
  final: z.boolean(),
  teams: z.array(KeepersTeam),
  updatedAt: Iso,
});
export type KeepersView = z.infer<typeof KeepersView>;

/** `PUT /api/leagues/:id/teams/:teamId/keepers`: the team's keepers, in order. None keeps nobody. */
export const KeepersRequest = z.object({
  playerIds: z.array(z.string().min(1)).max(40),
});
export type KeepersRequest = z.infer<typeof KeepersRequest>;
